import { useState, useEffect, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NginxVisitor } from '../../types';
import { formatNumber, getCountryFlag } from '../../constants/nginx-sites';
import * as nginxAnalyticsService from '../../services/nginx-analytics.service';

interface TopVisitorsTableProps {
  siteId: number;
  dateFrom: string;
  dateTo: string;
  accessToken: string;
}

export function TopVisitorsTable({ siteId, dateFrom, dateTo, accessToken }: TopVisitorsTableProps) {
  const [visitors, setVisitors] = useState<NginxVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    let active = true;
    const fetchVisitors = async () => {
      setLoading(true);
      try {
        const data = await nginxAnalyticsService.getTopVisitors(
          accessToken,
          siteId,
          dateFrom,
          dateTo,
          20
        );
        if (active) setVisitors(data.visitors);
      } catch (err) {
        if (active) console.error('Error fetching top visitors:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchVisitors();
    return () => { active = false; };
  }, [siteId, dateFrom, dateTo, accessToken]);

  const toggleRow = (ipHash: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ipHash)) {
        newSet.delete(ipHash);
      } else {
        newSet.add(ipHash);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Visitantes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  if (visitors.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Visitantes</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Visitante</TableHead>
              <TableHead>País</TableHead>
              <TableHead className="text-right">Requisições</TableHead>
              <TableHead>Canal</TableHead>
              <TableHead>Campanha UTM</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visitors.map((visitor) => {
              const isExpanded = expandedRows.has(visitor.ipHash);
              const deviceInfo = [visitor.deviceType, visitor.browser, visitor.os]
                .filter(Boolean)
                .join(' · ');
              const utmSourceMedium = visitor.utmSource
                ? `${visitor.utmSource}${visitor.utmMedium ? ` / ${visitor.utmMedium}` : ''}`
                : null;
              const utmCampaign = visitor.utmCampaign || null;

              return (
                <Fragment key={visitor.ipHash}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleRow(visitor.ipHash)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs">{visitor.ipPrefix}</span>
                      {deviceInfo && (
                        <div className="text-xs text-muted-foreground mt-0.5">{deviceInfo}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-base" title={visitor.countryCode || undefined}>
                        {getCountryFlag(visitor.countryCode)}
                        {visitor.countryCode && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            {visitor.countryCode}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(visitor.requestCount)}</TableCell>
                    <TableCell className="text-sm">{visitor.trafficChannel || '-'}</TableCell>
                    <TableCell>
                      {utmSourceMedium ? (
                        <>
                          <span className="text-sm">{utmSourceMedium}</span>
                          {utmCampaign && (
                            <div className="text-xs text-muted-foreground mt-0.5">{utmCampaign}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded && visitor.topPaths.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="pl-8 py-2">
                          <div className="text-xs font-semibold mb-2">Principais páginas visitadas:</div>
                          <ul className="list-disc list-inside text-xs space-y-1">
                            {visitor.topPaths.map((path) => (
                              <li key={path} className="font-mono">
                                {path}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
